import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import update from 'immutability-helper';
import { useCallback, useState, useRef, useEffect } from 'react';
import { useDrag, useDrop } from 'react-dnd'
import { FormControl, FormLabel, FormHelperText, FormGroup, FormControlLabel, Typography, MenuItem, Select, ListSubheader } from '@mui/material';
import DragIndicatorSharpIcon from '@mui/icons-material/DragIndicatorSharp';
import { Controller } from "react-hook-form";
import { TouchBackend } from 'react-dnd-touch-backend'

import './../App.css'

const ItemTypes = {
    CARD: 'card',
}

export const Card = ({ id, text, index, moveCard }) => {
  const ref = useRef(null)
  const [{ handlerId }, drop] = useDrop({
    accept: ItemTypes.CARD,
    collect(monitor) {
      return {
        handlerId: monitor.getHandlerId(),
      }
    },
    hover(item, monitor) {
      if (!ref.current) {
        return
      }
      const dragIndex = item.index
      const hoverIndex = index
      // Don't replace items with themselves
      if (dragIndex === hoverIndex) {
        return
      }
      // Determine rectangle on screen
      const hoverBoundingRect = ref.current?.getBoundingClientRect()
      // Get vertical middle
      const hoverMiddleY =
        (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2
      // Determine mouse position
      const clientOffset = monitor.getClientOffset()
      // Get pixels to the top
      const hoverClientY = clientOffset.y - hoverBoundingRect.top
      // Only perform the move when the mouse has crossed half of the items height
      // When dragging downwards, only move when the cursor is below 50%
      // When dragging upwards, only move when the cursor is above 50%
      // Dragging downwards
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return
      }
      // Dragging upwards
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return
      }
      // Time to actually perform the action
      moveCard(dragIndex, hoverIndex)
      // Note: we're mutating the monitor item here!
      // Generally it's better to avoid mutations,
      // but it's good here for the sake of performance
      // to avoid expensive index searches.
      item.index = hoverIndex
    },
  })

  const [{ isDragging }, drag] = useDrag({
    type: ItemTypes.CARD,
    item: () => {
      return { id, index }
    },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  })
  const opacity = isDragging ? 0.5 : 1
  const border = isDragging ? '1px solid #ff9800' : '1px dashed rgba(255, 255, 255, 0.3)';

  drag(drop(ref))

  return (
    <div ref={ref} className="drag-card" style={{ opacity, border}} data-handler-id={handlerId}>
      <div className="drag-card-index font-14" >{index+1 + " "}</div>
      <div>{text}</div>
      <DragIndicatorSharpIcon className="drag-card-icon"/>
    </div>
  )
}


const Container = ({items, useCards}) => {
    const [cards, setCards] = useState(items)
    const moveCard = useCallback((dragIndex, hoverIndex) => {
      setCards((prevCards) =>
        update(prevCards, {
          $splice: [
            [dragIndex, 1],
            [hoverIndex, 0, prevCards[dragIndex]],
          ],
        }),
      )
    }, [])
    
    useCards(cards);

    const renderCard = useCallback((card, index) => {
      return (
        <Card
          key={card.id}
          index={index}
          id={card.id}
          text={card.text}
          moveCard={moveCard}
        />
      )
    }, [moveCard])
    
    return (
        <FormGroup>
        {cards.map((card, i) => (
            <FormControlLabel key={i} control={renderCard(card, i)}/>
        ))}
        </FormGroup>
    )
};


export default function SortableList({name, control, setValue, errors, question, helper, items, required}) {
    const [firstOption, setFirstOption] = useState(items[0].text);
    const [secondOption, setSecondOption] = useState(items[1].text);
    const [thirdOption, setThirdOption] = useState(items[2].text);
  const isTouchScreen = () => {
    return (( 'ontouchstart' in window ) ||
           ( navigator.maxTouchPoints > 0 ) ||
           ( navigator.msMaxTouchPoints > 0 ))
    };
    useEffect(() => {
        setValue(name, [{id:"1,", text:items[0].text}, {id:"2,",text:items[1].text}, {id:"3,",text:items[2].text}])
    }, [])


  const useCards = (cards) => {
    useEffect(() => {
      setValue(name, cards);
    }, [cards]);
  }
  const handleChange = (value) => {
      let newValue = value.target.value
      switch (value.target.name) {
          case "1":
              setFirstOption(newValue)
              setValue(name, [{id:"1,", text:newValue}, {id:"2,",text:secondOption}, {id:"3,",text:thirdOption}])
              break
          case "2":
              setSecondOption(newValue)
              setValue(name, [{id:"1,", text:firstOption}, {id:"2,",text:newValue}, {id:"3,",text:thirdOption}])
              break
          case "3":
              setThirdOption(newValue)
              setValue(name, [{id:"1,", text:firstOption}, {id:"2,",text:secondOption}, {id:"3,",text:newValue}])
              break
      }

  }

    return (
      <Controller
        name={name}
        control={control}
        render={() =>
            <div>
                <FormControl fullWidth error={errors? true : false}>
                    <FormLabel required={required}>{question}</FormLabel>
                    {helper? helper.map(text => (
                        <FormHelperText key={text} className="wrap-it helper">{text}</FormHelperText>
                    )) : null}
                    <Select
                        name='1'
                        labelId="select1"
                        id="select1"
                        value={firstOption}
                        onChange={handleChange}
                    >
                        <MenuItem value={items[0].text}>{items[0].text}</MenuItem>
                        <MenuItem value={items[1].text}>{items[1].text}</MenuItem>
                        <MenuItem value={items[2].text}>{items[2].text}</MenuItem>
                    </Select>
                </FormControl>
                <FormControl fullWidth error={errors? true : false}>
                    <Select
                        name='2'
                        labelId="select2"
                        id="select2"
                        value={secondOption}
                        onChange={handleChange}
                    >
                        <MenuItem value={items[0].text}>{items[0].text}</MenuItem>
                        <MenuItem value={items[1].text}>{items[1].text}</MenuItem>
                        <MenuItem value={items[2].text}>{items[2].text}</MenuItem>
                    </Select>
                </FormControl>
                <FormControl fullWidth error={errors? true : false}>
                    <Select
                        name='3'
                        labelId="select3"
                        id="select3"
                        value={thirdOption}
                        onChange={handleChange}
                    >
                        <MenuItem value={items[0].text}>{items[0].text}</MenuItem>
                        <MenuItem value={items[1].text}>{items[1].text}</MenuItem>
                        <MenuItem value={items[2].text}>{items[2].text}</MenuItem>
                    </Select>
                    <Typography className="font-14 error-message">
                        {errors?.message}
                    </Typography>
                </FormControl>
            </div>

        }
      />      
    )
}